import type { RefObject } from 'react';
import { SelectionFloatingActions } from './SelectionFloatingActions';
import { CursorStatementActions } from './CursorStatementActions';
import { CursorStatementGutterActions } from './CursorStatementGutterActions';
import { PeekResultsOverlay } from './PeekResultsOverlay';
import { CompactVqePopover, type CompactVqeSource } from '../../visual-query-editor/compact/CompactVqePopover';
import { resolveShowRunActions } from './showRunActions';
import { TableSeedAffordance } from '../table-seed/TableSeedAffordance';
import { TableSeedPicker } from '../table-seed/TableSeedPicker';
import type { TableSeedState } from '../table-seed/useTableSeed';
import type { QueryViewModeTarget } from '../visual-mode/useQueryViewMode';
import type { QueryEditorHandle } from './QueryEditor';
import type { PeekResultsState } from './PeekResultsPanel';
import type { PeekAnchor } from '../hooks/usePeekResults';
import type { CursorStatement, RawCursorSegment } from '../hooks/useCursorStatementTracking';
import type { CursorStatementEditInfo } from '../../visual-query-editor/compact/useCursorStatementEditInfo';
import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { DatabaseEngineId } from '../../../../core/engines';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';

type CompactEditState = { open: boolean; source: CompactVqeSource };

type QueryEditorOverlaysProps = {
  tabId: string;
  engineId: DatabaseEngineId;
  connectionId: string | undefined;
  tabDatabase: string | undefined;
  editorRef: RefObject<QueryEditorHandle | null>;
  /** Debounced React query state (same value the edit-context hosts are fed). */
  query: string;
  showInlineExecuteBar: boolean;
  editorSelectedText: string;
  editorSelectedRange: EditorSelectionRange | null;
  isExecuting: boolean;
  isPeekExecuting: boolean;
  isGeneratingScript: boolean;
  affordanceStatement: CursorStatement | RawCursorSegment | null;
  /** The pencil's statement: `affordanceStatement`, else the one a selection covers exactly. */
  editStatement: CursorStatement | RawCursorSegment | null;
  currentCursorStatement: CursorStatement | null;
  /** Ungated raw segment under the caret — hides the table-seed glyph inside any statement. */
  rawCursorSegment: RawCursorSegment | null;
  /** "Query a table" seed flow (`useTableSeed`). */
  tableSeed: TableSeedState;
  cursorEditInfo: CursorStatementEditInfo;
  vqeParseOptions: ParseOptions;
  compactEdit: CompactEditState;
  setCompactEdit: (updater: (prev: CompactEditState) => CompactEditState) => void;
  openCompactEdit: (source: CompactVqeSource) => void;
  peekState: PeekResultsState | null;
  peekAnchor: PeekAnchor | null;
  closePeek: () => void;
  handleOpenPeekInResultsAndClose: (peekQuery: string) => void;
  handleExecuteFromSelectionBar: (sql: string) => void;
  handleExecuteFromCursorBar: (sql: string) => void;
  handlePeek: (sql: string) => void;
  /** Switch this tab to its Visual view (the compact editor's "Open full editor"). */
  onOpenVisualView: (target: QueryViewModeTarget) => void;
};

/**
 * The overlay layer rendered over a query editor tab's Monaco mount: the
 * floating selection / cursor-statement Execute+Peek bars (or their gutter
 * fallback), the Peek Results popup, the Compact VQE popover, the inline DDL
 * toolbar host, and the script-generation loading overlay. Pure JSX moved
 * verbatim from QueryEditorTabContent — all state and handlers stay with the
 * host's hooks.
 */
export function QueryEditorOverlays({
  tabId,
  engineId,
  connectionId,
  tabDatabase,
  editorRef,
  query,
  showInlineExecuteBar,
  editorSelectedText,
  editorSelectedRange,
  isExecuting,
  isPeekExecuting,
  isGeneratingScript,
  affordanceStatement,
  editStatement,
  currentCursorStatement,
  rawCursorSegment,
  tableSeed,
  cursorEditInfo,
  vqeParseOptions,
  compactEdit,
  setCompactEdit,
  openCompactEdit,
  peekState,
  peekAnchor,
  closePeek,
  handleOpenPeekInResultsAndClose,
  handleExecuteFromSelectionBar,
  handleExecuteFromCursorBar,
  handlePeek,
  onOpenVisualView,
}: QueryEditorOverlaysProps) {
  // While text is selected `cursorEditInfo` classifies the statement the
  // selection covers exactly (`editStatement`), so the selection bar's Edit
  // and the gutter pencil in selection mode edit that whole statement.
  const canEdit = cursorEditInfo.target !== null && !isGeneratingScript;
  return (
    <>
      {/* Floating selection + cursor-statement bars (Execute + Peek) —
          appear next to any selected SQL and above the statement under
          the cursor. Both are gated behind the same `showInlineExecuteBar`
          setting. When the setting is OFF the gutter shows small
          Play/Peek icons for the cursor's statement instead. The toolbar
          Execute button relabels to "Selection" for runnable selections
          in BOTH modes — that behaviour is not tied to this setting.

          `statementRange`/`statementText` intentionally read the wider
          `affordanceStatement` (pencil-reachable OR a confirmed-broken
          raw segment — EDG-11..15) so the parse-error chip/squiggle can
          attach to a broken statement. `showRunActions` intentionally
          stays on the NARROWER `currentCursorStatement` (VQE Polish,
          EDG-fix-hardening M2): Run/Peek must never be offered on a
          statement that only qualified via the broken-statement path —
          running it would just error at the engine. Keep these two
          decoupled; see `resolveShowRunActions` in `showRunActions.ts`
          + `showRunActions.test.ts`. */}
      {showInlineExecuteBar && (
        <>
          <SelectionFloatingActions
            editorRef={editorRef}
            selectedText={editorSelectedText}
            editorContent={query}
            isExecuting={isExecuting}
            isPeekExecuting={isPeekExecuting}
            onExecute={handleExecuteFromSelectionBar}
            onPeek={handlePeek}
            canEdit={canEdit}
            onEdit={() => openCompactEdit('inline')}
          />
          <CursorStatementActions
            editorRef={editorRef}
            editorContent={query}
            statementRange={affordanceStatement?.range ?? null}
            statementText={affordanceStatement?.text ?? ''}
            engineId={engineId}
            isExecuting={isExecuting}
            isPeekExecuting={isPeekExecuting}
            showRunActions={resolveShowRunActions(currentCursorStatement)}
            onExecute={handleExecuteFromCursorBar}
            onPeek={handlePeek}
            canEdit={canEdit}
            onEdit={() => openCompactEdit('inline')}
            unavailableReason={cursorEditInfo.unavailableReason}
          />
        </>
      )}
      {!showInlineExecuteBar && (
        <CursorStatementGutterActions
          editorRef={editorRef}
          statementRange={affordanceStatement?.range ?? null}
          statementText={affordanceStatement?.text ?? ''}
          selectionRange={editorSelectedRange}
          selectionText={editorSelectedText}
          editorContent={query}
          engineId={engineId}
          isExecuting={isExecuting}
          isPeekExecuting={isPeekExecuting}
          showRunActions={resolveShowRunActions(currentCursorStatement)}
          onExecuteStatement={handleExecuteFromCursorBar}
          onExecuteSelection={handleExecuteFromSelectionBar}
          onPeekStatement={handlePeek}
          onPeekSelection={handlePeek}
          canEdit={canEdit}
          onEditStatement={() => openCompactEdit('gutter')}
          unavailableReason={cursorEditInfo.unavailableReason}
        />
      )}
      {/* "Query a table" — fills the gutter slot the pencil leaves empty when the
          caret sits on a blank line outside every statement. Same gutter-vs-
          inline split as the Execute / Peek / Edit affordances above. Picking
          a table seeds `SELECT <first N rows> * FROM …;` at the caret, runs it,
          and opens the compact VQE popover on it. */}
      <TableSeedAffordance
        editorRef={editorRef}
        enabled={tableSeed.enabled}
        cursorStatementRange={rawCursorSegment?.range ?? affordanceStatement?.range ?? null}
        hasSelection={editorSelectedText.trim().length > 0}
        isEmptyBuffer={query.trim().length === 0}
        inline={showInlineExecuteBar}
        onOpen={(anchorTop) => tableSeed.open(showInlineExecuteBar ? 'inline' : 'gutter', anchorTop)}
      />
      <TableSeedPicker
        open={tableSeed.picker.open}
        onOpenChange={tableSeed.setOpen}
        anchorTop={tableSeed.picker.anchorTop}
        engineId={engineId}
        groups={tableSeed.groups}
        loading={tableSeed.loading}
        loadError={tableSeed.loadError}
        onRetry={tableSeed.retryLoad}
        onPick={tableSeed.handlePick}
      />
      {/* Peek Results popup overlay (rendered over the editor — does not
          push editor content down or trigger an editor scrollbar). */}
      <PeekResultsOverlay
        editorRef={editorRef}
        state={peekState}
        anchor={peekAnchor}
        onClose={closePeek}
        onOpenInResults={handleOpenPeekInResultsAndClose}
      />
      {/* Compact Visual Query Editor popover — opened on demand by the
          pencil edit affordance (gutter icon / inline button), anchored
          to the cursor statement. Replaces the always-on inline VQE
          (DML) toolbar (WS2); opens for SELECT/DML (edit sections) AND
          for the 7 true-DDL statement kinds (read-only summary card,
          §2.9 S13) — everything else (EXPLAIN/SET/procedural/parse-error/…)
          still gets no pencil at all. */}
      <CompactVqePopover
        editorRef={editorRef}
        tabId={tabId}
        query={query}
        mode={cursorEditInfo.mode}
        statementId={cursorEditInfo.statementId}
        statementText={editStatement?.text ?? ''}
        editTarget={cursorEditInfo.target}
        statementRange={editStatement?.range ?? null}
        connectionId={connectionId ?? null}
        database={tabDatabase ?? null}
        parseOptions={vqeParseOptions}
        open={
          compactEdit.open &&
          (cursorEditInfo.target === 'compact' || cursorEditInfo.target === 'ddl-card')
        }
        source={compactEdit.source}
        onOpenChange={(open) => setCompactEdit((prev) => ({ ...prev, open }))}
        // "Open full editor" → this tab's Visual view on the same statement,
        // keeping the popover's drill (`feature:vqe_mode_toggle`
        // `compact-promote`, distinct from `feature:vqe_open_compact`).
        onOpenFull={(drill) =>
          onOpenVisualView({ statementOffset: editStatement?.startOffset, drill })
        }
      />
      {/* Structural DDL editing (VqeDdlEdit) now lives entirely inside the
          Compact VQE popover above (`visual-ddl-editor/compact/CompactDdlEditor`,
          opened via the same pencil edit affordance as SELECT/DML) — the
          always-on inline DDL toolbar that used to float above the parked
          statement was removed. */}
      {/* Loading overlay for script generation */}
      {isGeneratingScript && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <RandomDotmSpinner size={32} className="text-primary" />
            <span className="text-sm font-medium">Generating script...</span>
            <span className="text-xs">Fetching table metadata</span>
          </div>
        </div>
      )}
    </>
  );
}
